import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { DashboardShell } from "@/components/DashboardShell";
import { CUSTOMER_NAV } from "@/lib/nav";
import { useAuth } from "@/lib/auth";
import {
  REPORT_REASON_LABEL,
  REPORT_STATUSES,
  REPORT_STATUS_CLASS,
  REPORT_STATUS_LABEL,
  listMyReports,
  type ReportStatus,
} from "@/lib/abuse-reports";

type Tab = "all" | ReportStatus;

export const Route = createFileRoute("/_authenticated/customer/reports")({
  head: () => ({
    meta: [
      { title: "My Reports · Landoo" },
      {
        name: "description",
        content:
          "Track the listings and partners you reported on Landoo and see how our team resolved them.",
      },
      { property: "og:title", content: "My Reports · Landoo" },
      {
        property: "og:description",
        content:
          "Track the listings and partners you reported on Landoo and see how our team resolved them.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: CustomerReportsPage,
});

function CustomerReportsPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>("all");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["customer", "abuse-reports", user?.id],
    queryFn: () => listMyReports(user!.id),
    enabled: !!user,
  });

  const list = useMemo(
    () => (tab === "all" ? rows : rows.filter((r) => r.status === tab)),
    [rows, tab],
  );

  return (
    <DashboardShell title="My Reports" role="Customer" items={CUSTOMER_NAV}>
      <div className="flex flex-wrap gap-1.5">
        {(["all", ...REPORT_STATUSES] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
              tab === t ? "bg-brown-dark text-white" : "bg-secondary text-brown-dark"
            }`}
          >
            {t === "all" ? "All" : REPORT_STATUS_LABEL[t]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <p className="mt-6 text-sm text-muted-foreground">Loading your reports…</p>
      ) : list.length === 0 ? (
        <p className="mt-6 rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          You haven't reported anything in this view. Use the Report button on a listing or partner
          profile.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {list.map((r) => (
            <li key={r.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${REPORT_STATUS_CLASS[r.status]}`}
                >
                  {REPORT_STATUS_LABEL[r.status]}
                </span>
                <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] font-semibold capitalize text-brown-dark">
                  {r.target_type}
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {new Date(r.created_at).toLocaleString()}
                </span>
              </div>
              <p className="mt-1 text-sm font-semibold text-brown-dark">
                {r.listing_title ?? r.partner_name ?? "Reported item"}
              </p>
              <p className="text-xs text-muted-foreground">{REPORT_REASON_LABEL[r.reason]}</p>
              {r.comment && <p className="mt-2 text-sm text-brown-dark">“{r.comment}”</p>}
              {(r.action_taken || r.admin_notes) && (
                <div className="mt-2 rounded-xl bg-secondary/60 p-3 text-xs text-brown-dark">
                  {r.action_taken && <div className="font-semibold">Action: {r.action_taken}</div>}
                  {r.admin_notes && <div className="mt-0.5">{r.admin_notes}</div>}
                  {r.resolved_at && (
                    <div className="mt-0.5 text-muted-foreground">
                      Reviewed on {new Date(r.resolved_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </DashboardShell>
  );
}
